import { type PathParams } from "@constellation-academy/epic-ui";
import { Card } from "primereact/card";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useFragment, useLazyLoadQuery, useSubscription } from "react-relay";
import { useParams } from "react-router-dom";
import { type GraphQLSubscriptionConfig } from "relay-runtime";
import { useTranslation } from "@i18n/hooks";
import { onWSReconnected } from "@network/WSSubscription";
import { type educationalOffer_PublishingV2Fragment$key } from "@relay/educationalOffer_PublishingV2Fragment.graphql";
import { type educationalOffer_Query } from "@relay/educationalOffer_Query.graphql";
import { type educationalOffer_Subscription } from "@relay/educationalOffer_Subscription.graphql";
import { type educationalOffer_TreeFragment$key } from "@relay/educationalOffer_TreeFragment.graphql";
import {
	PUBLISHINGV2_FRAGMENT,
	QUERY,
	SUBSCRIPTION,
	TREE_FRAGMENT,
} from "./educational-offer.graphql";
import { type NodePath, type EducationalOfferPath } from "./educational-offer.paths";
import { wrapperClass } from "./educational-offer.styles";
import { type EducationalOfferScreenProps } from "./educational-offer.types";
import { EducationalOfferTree } from "./parts/educational-offer-tree";
import { NodePart } from "./parts/node-part";
import { TreeSelectionContainer } from "./parts/TreeSelectionContext";
import { BaseScreen } from "../../components/base-screen";
import { BaseScreenNoBackground } from "../../components/base-screen-no-background";
import { NoAccess } from "../../components/no-access";
import { useHasPermissions } from "../../hooks/use-has-permissions";

const EducationalOfferScreenComponent = ({ rootId }: EducationalOfferScreenProps) => {
	const { t } = useTranslation("common");
	const { nodeId } = useParams<PathParams<typeof NodePath>>();
	const [fetchKey, setFetchKey] = useState(0);

	const query = useLazyLoadQuery<educationalOffer_Query>(
		QUERY,
		{
			input: rootId,
		},
		{ fetchPolicy: "network-only", fetchKey },
	);

	const tree = useFragment<educationalOffer_TreeFragment$key>(
		TREE_FRAGMENT,
		query.Admin.Tree.Tree,
	);

	const publishing = useFragment<educationalOffer_PublishingV2Fragment$key>(
		PUBLISHINGV2_FRAGMENT,
		query.Admin.PublishingV2.ActivePublishing ?? null,
	);

	const config = useMemo<GraphQLSubscriptionConfig<educationalOffer_Subscription>>(
		() => ({
			variables: {},
			subscription: SUBSCRIPTION,
		}),
		[],
	);

	useSubscription<educationalOffer_Subscription>(config);

	useEffect(() => onWSReconnected(() => setFetchKey((key) => key + 1)), []);

	const canReadAndModify = useHasPermissions([
		"UserInAccountPermission_Nodes_ReadNodes",
		"UserInAccountPermission_Nodes_UpdateNodes",
	]);

	if (!canReadAndModify) return <NoAccess />;

	return (
		<BaseScreen>
			<TreeSelectionContainer treeId={rootId} initiallySelectedNodeId={nodeId ?? rootId}>
				<div className={wrapperClass}>
					<EducationalOfferTree
						treeFragmentRef={tree}
						publishingV2FragmentRef={publishing}
						onPublishingFailed={() => {
							setFetchKey((key) => key + 1);
						}}
					/>
					<Suspense
						fallback={
							<BaseScreenNoBackground>
								<Card>{t("general.loading")}</Card>
							</BaseScreenNoBackground>
						}
					>
						<NodePart publishingV2FragmentRef={publishing} />
					</Suspense>
				</div>
			</TreeSelectionContainer>
		</BaseScreen>
	);
};

export const EducationalOfferScreen = () => {
	const params = useParams<PathParams<typeof EducationalOfferPath>>();
	return params.rootId ? <EducationalOfferScreenComponent rootId={params.rootId} /> : null;
};
